import type { App } from "@colorglo/api";
import { treaty } from "@elysiajs/eden";

/**
 * The typed API client.
 *
 * Eden Treaty derives this from `App`'s **type**, not from a generated artefact — so the
 * import below is `import type`, nothing from `apps/api` reaches a browser bundle, and
 * there is no codegen step to forget to re-run. Rename a route or change a response shape
 * in the API and `apps/app` stops compiling in the same commit, which is the entire point:
 * a client and a server that disagree should be a build failure and not a support ticket.
 *
 * ```ts
 * const api = createClient(import.meta.env.VITE_API_URL);
 *
 * const { data, error } = await api.v1.franchises({ slug: "helsingborg" }).availability.get({
 *   query: { serviceIds: id, from: "2026-08-25", to: "2026-09-07" },
 * });
 * if (error) return;            // `error.value` is the { error: { code, … } } envelope
 * data.days[0]?.slots;          // fully inferred, no cast
 * ```
 *
 * `error` is narrowed by status, so a `409` from `POST /v1/bookings` gives typed access to
 * `details.alternatives` without a cast — which is what makes the conflict path pleasant to
 * build against rather than something a frontend guesses at.
 */

export interface ClientOptions {
  /**
   * Sent on every request, so the session cookie travels. The cookie is HttpOnly and
   * `SameSite=Lax` (P3), so this is the only way the staff app authenticates — there is no
   * token to attach and nothing in `localStorage` to steal.
   */
  readonly credentials?: "include" | "omit" | "same-origin";
  readonly headers?: Record<string, string>;
  /** For tests: hand it a `fetch` that routes into the Elysia app, skipping the network. */
  readonly fetcher?: typeof fetch;
}

export function createClient(baseUrl: string, options: ClientOptions = {}) {
  return treaty<App>(baseUrl, {
    /**
     * **Do not remove this.** Eden's default is `true`, and the default is wrong for this API.
     *
     * Treaty installs a `JSON.parse` reviver that turns anything matching an ISO 8601 pattern
     * into a `Date`. Every instant this API returns matches — that is the convention
     * (docs/API.md) — so `booking.start` would arrive as a `Date` rather than the
     * `2026-08-25T09:00:00+02:00` the server sent.
     *
     * That breaks two things at once. It is precisely the type CLAUDE.md bans from
     * user-facing time handling, so the next person to touch a booking reaches for
     * `getTimezoneOffset()`. And it is lossy: a `Date` is an instant with no offset and no
     * zone, so the offset the server was careful to send is gone by the time anything reads
     * it — which is the exact failure the "explicit offset plus a separate IANA zone"
     * convention exists to prevent.
     *
     * With this off, an instant stays the string it was on the wire and the only thing that
     * can be done with it is to hand it to Temporal along with a timezone. `packages/db`
     * makes the same choice at the other end of the system, for the same reason.
     */
    parseDate: false,
    fetch: { credentials: options.credentials ?? "include" },
    // Spread rather than assign: `exactOptionalPropertyTypes` is on, so an explicit
    // `undefined` is not the same as an absent key, and Eden's config means the second.
    ...(options.headers === undefined ? {} : { headers: options.headers }),
    ...(options.fetcher === undefined ? {} : { fetcher: options.fetcher }),
  });
}

export type ApiClient = ReturnType<typeof createClient>;

/**
 * The error envelope every `/v1` route returns (docs/API.md).
 *
 * Declared here rather than imported from the API because clients switch on `code` and
 * should not need the server's module graph to do it. `code` is stable and machine-readable;
 * `message` is already localized server-side and is meant to be displayed as-is.
 */
export interface ApiErrorEnvelope {
  readonly error: {
    readonly code: string;
    readonly message: string;
    readonly details?: Record<string, unknown>;
    readonly request_id: string | null;
  };
}

/** The shape of `details` on a 409 from a booking write. */
export interface SlotUnavailableDetails {
  /** Up to three nearby start times, ISO 8601 with offsets. May be empty. */
  readonly alternatives: readonly string[];
  readonly timezone: string;
}

export function isApiError(value: unknown): value is ApiErrorEnvelope {
  return (
    typeof value === "object" &&
    value !== null &&
    "error" in value &&
    typeof (value as ApiErrorEnvelope).error?.code === "string"
  );
}
